import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, RefreshCw, Trash2, Book, Link as LinkIcon, FileText, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SOURCE_LABELS = {
    text: { label: 'Texte', icon: FileText, color: 'bg-slate-100 text-slate-700' },
    url: { label: 'URL externe', icon: LinkIcon, color: 'bg-blue-100 text-blue-700' },
    crm_document: { label: 'Doc CRM', icon: Book, color: 'bg-emerald-100 text-emerald-700' },
    crm_module: { label: 'Module Academy', icon: Database, color: 'bg-purple-100 text-purple-700' },
};

/**
 * Panneau Admin de gestion de la Base de Connaissances Uatu.
 * Réservé admin_full. Permet d'ajouter :
 *  - un texte libre
 *  - une URL externe (re-fetchée auto toutes les 24h)
 *  - un document CRM existant (useful_documents)
 *  - un module Academy existant
 */
export default function UatuKbAdmin({ focusDocId }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [flashId, setFlashId] = useState(null);

    const [form, setForm] = useState({
        title: '',
        category: 'général',
        source_type: 'text',
        source_url: '',
        source_ref_id: '',
        content: '',
    });

    const [crmDocs, setCrmDocs] = useState([]);
    const [crmModules, setCrmModules] = useState([]);

    const loadDocs = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/uatu/kb');
            setDocs(r.data?.items || []);
        } catch {
            toast.error('Impossible de charger la base de connaissances');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadCrmRefs = useCallback(async () => {
        try {
            const [d, m] = await Promise.all([
                api.get('/uatu/kb/browse/crm-documents'),
                api.get('/uatu/kb/browse/crm-modules'),
            ]);
            setCrmDocs(d.data?.items || []);
            setCrmModules(m.data?.items || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { loadDocs(); loadCrmRefs(); }, [loadDocs, loadCrmRefs]);

    // Deep-link Uatu → focus + scroll sur un doc précis (clic sur badge KB dans le chat).
    // Effet ring violet 3s pour attirer l'oeil, puis retour à l'affichage normal.
    useEffect(() => {
        if (!focusDocId || loading || docs.length === 0) return;
        if (!docs.some((d) => d.id === focusDocId)) return;
        setFlashId(focusDocId);
        const el = document.querySelector(`[data-testid="kb-item-${focusDocId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const t = setTimeout(() => setFlashId(null), 3000);
        return () => clearTimeout(t);
    }, [focusDocId, loading, docs]);

    const submit = async () => {
        if (!form.title.trim()) return toast.error('Titre requis');
        if (form.source_type === 'url' && !form.source_url.trim()) return toast.error('URL requise');
        if (form.source_type === 'text' && !form.content.trim()) return toast.error('Contenu requis');
        if ((form.source_type === 'crm_document' || form.source_type === 'crm_module') && !form.source_ref_id)
            return toast.error('Choisis un élément CRM');

        setCreating(true);
        try {
            const payload = { title: form.title, category: form.category, source_type: form.source_type };
            if (form.source_type === 'url') payload.source_url = form.source_url;
            else if (form.source_type === 'text') payload.content = form.content;
            else payload.source_ref_id = form.source_ref_id;
            await api.post('/uatu/kb', payload);
            toast.success(`Document ajouté à la KB`);
            setForm({ title: '', category: 'général', source_type: 'text', source_url: '', source_ref_id: '', content: '' });
            loadDocs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Échec de l\'ajout');
        } finally {
            setCreating(false);
        }
    };

    const refresh = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/uatu/kb/${id}/refresh`);
            toast.success('Contenu rafraîchi');
            loadDocs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Rafraîchissement impossible');
        } finally { setBusyId(null); }
    };

    const remove = async (id) => {
        if (!window.confirm('Supprimer ce document de la base de connaissances ?')) return;
        setBusyId(id);
        try {
            await api.delete(`/uatu/kb/${id}`);
            toast.success('Supprimé');
            loadDocs();
        } catch { toast.error('Suppression impossible'); }
        finally { setBusyId(null); }
    };

    return (
        <Card className="border-violet-200" data-testid="uatu-kb-admin">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <Book className="w-4 h-4 text-violet-600" />
                    Base de connaissances Uatu
                    {docs.length > 0 && <Badge variant="secondary" className="text-[10px]">{docs.length}</Badge>}
                </CardTitle>
                <CardDescription>
                    Ajoute des règles métier, procédures ou modules pour qu&apos;Uatu les cite mot-à-mot avec la source.
                    Les URLs et références CRM sont rafraîchies automatiquement toutes les 24h.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Formulaire d'ajout */}
                <div className="rounded-lg border border-dashed border-violet-300 bg-violet-50/50 p-3 space-y-2">
                    <p className="text-xs font-semibold text-violet-800 uppercase tracking-wide">Ajouter un document</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">Titre</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="Ex : Grille tarifs 2026" className="h-9" data-testid="kb-form-title" />
                        </div>
                        <div>
                            <Label className="text-xs">Catégorie</Label>
                            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                placeholder="procédure / tarifs / academy" className="h-9" data-testid="kb-form-category" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Source</Label>
                        <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v, source_url: '', source_ref_id: '', content: '' })}>
                            <SelectTrigger className="h-9" data-testid="kb-form-source-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="text">📝 Texte libre</SelectItem>
                                <SelectItem value="url">🔗 URL externe (re-fetch 24h)</SelectItem>
                                <SelectItem value="crm_document">📚 Document du CRM (Outils & liens utiles)</SelectItem>
                                <SelectItem value="crm_module">🎓 Module Academy</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {form.source_type === 'text' && (
                        <div>
                            <Label className="text-xs">Contenu</Label>
                            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                                rows={5} placeholder="Colle ici la règle, la procédure, le tarif…" data-testid="kb-form-content" />
                        </div>
                    )}
                    {form.source_type === 'url' && (
                        <div>
                            <Label className="text-xs">URL</Label>
                            <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                                placeholder="https://academy.valnexo.com/module-cee" className="h-9" data-testid="kb-form-url" />
                            <p className="text-[10px] text-slate-500 mt-1">Uatu re-fetchera cette URL toutes les 24h automatiquement.</p>
                        </div>
                    )}
                    {form.source_type === 'crm_document' && (
                        <div>
                            <Label className="text-xs">Document CRM ({crmDocs.length} disponibles)</Label>
                            <Select value={form.source_ref_id} onValueChange={(v) => setForm({ ...form, source_ref_id: v })}>
                                <SelectTrigger className="h-9" data-testid="kb-form-crm-doc"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {crmDocs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.title} {d.category && `(${d.category})`}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {form.source_type === 'crm_module' && (
                        <div>
                            <Label className="text-xs">Module Academy ({crmModules.length} disponibles)</Label>
                            <Select value={form.source_ref_id} onValueChange={(v) => setForm({ ...form, source_ref_id: v })}>
                                <SelectTrigger className="h-9" data-testid="kb-form-crm-module"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {crmModules.map((m) => (<SelectItem key={m.id} value={m.id}>{m.title} {m.level && `— ${m.level}`}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <Button size="sm" onClick={submit} disabled={creating} data-testid="kb-form-submit"
                            className="bg-violet-600 hover:bg-violet-700 gap-1">
                            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                            Ajouter à la KB
                        </Button>
                    </div>
                </div>

                {/* Liste */}
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                    </div>
                ) : docs.length === 0 ? (
                    <p className="text-sm text-slate-400 italic py-4 text-center">
                        Aucun document dans la KB pour l&apos;instant.
                    </p>
                ) : (
                    <div className="space-y-1.5" data-testid="kb-list">
                        {docs.map((d) => {
                            const meta = SOURCE_LABELS[d.source_type] || SOURCE_LABELS.text;
                            const Icon = meta.icon;
                            return (
                                <div key={d.id}
                                    className={`rounded-lg border p-2.5 flex items-start gap-3 transition-shadow ${
                                        flashId === d.id
                                            ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-300'
                                            : 'border-slate-200 bg-white'
                                    }`}
                                    data-testid={`kb-item-${d.id}`}>
                                    <span className={`shrink-0 rounded-md p-1.5 ${meta.color}`}><Icon className="w-3.5 h-3.5" /></span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-800 truncate">{d.title}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{d.category}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                                            {d.content_length} car. · Ajouté par {d.created_by_email || 'inconnu'}
                                            {d.fetched_at && ` · Rafraîchi ${new Date(d.fetched_at).toLocaleDateString('fr-FR')}`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {['url', 'crm_document', 'crm_module'].includes(d.source_type) && (
                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => refresh(d.id)}
                                                disabled={busyId === d.id} title="Rafraîchir"
                                                data-testid={`kb-refresh-${d.id}`}>
                                                <RefreshCw className={`w-3.5 h-3.5 ${busyId === d.id ? 'animate-spin' : ''}`} />
                                            </Button>
                                        )}
                                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => remove(d.id)}
                                            disabled={busyId === d.id} title="Supprimer"
                                            data-testid={`kb-delete-${d.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
